import type { LipDetection } from '../tracking/faceLandmarker';
import { stackBlur } from '../rendering/stackBlur';
import { maskAlphaCoverage } from '../rendering/maskUtils';

export type SemanticMaskSource = 'landmark-pseudo' | 'face-parsing';

export interface SemanticFaceMap {
  width: number;
  height: number;
  source: SemanticMaskSource;
  masks: {
    faceOval: HTMLCanvasElement;
    skin: HTMLCanvasElement;
    leftEye: HTMLCanvasElement;
    rightEye: HTMLCanvasElement;
    leftMobileLid: HTMLCanvasElement;
    rightMobileLid: HTMLCanvasElement;
    leftCreaseFade: HTMLCanvasElement;
    rightCreaseFade: HTMLCanvasElement;
    leftLashLine: HTMLCanvasElement;
    rightLashLine: HTMLCanvasElement;
    leftBrow: HTMLCanvasElement;
    rightBrow: HTMLCanvasElement;
    lips: HTMLCanvasElement;
    lipBorder: HTMLCanvasElement;
    leftCheek: HTMLCanvasElement;
    rightCheek: HTMLCanvasElement;
    // Optional: only the ONNX face-parsing path produces a hair mask (landmarks
    // can't derive hair). Consumed by the hair-recolour spike (hairOverlay).
    hair?: HTMLCanvasElement;
    // Optional: ONNX-only ear mask, subtracted from the hair recolour so cool
    // shades don't cast grey on the (foreground) ears surrounded by hair.
    ears?: HTMLCanvasElement;
    // Optional: ONNX-only cloth mask (collar, shirt). Subtracted from the hair
    // recolour zone so dark shades don't tint a white collar through MODNet's
    // person-alpha (both hair and collar are foreground).
    cloth?: HTMLCanvasElement;
    // Optional: ONNX-only FULL face skin (face-oval skin, hair NOT carved out).
    // Distinct from `skin`, which is the cosmetic skin with hair/features removed
    // for makeup passes. The hair recolour needs the full skin to subtract: the
    // hair mask is grown to over-reach the hairline (makeup protection), and the
    // cosmetic skin has a hair-shaped hole there, so it can't claw that overreach
    // back off the forehead/temple/cheek. See hairOverlay.
    faceSkinFull?: HTMLCanvasElement;
  };
  quality: {
    confidence: number;
    parserAvailable: boolean;
    coverage: Record<string, number>;
    fallbacks: string[];
  };
}

const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

const RIGHT_EYE_UPPER_LID = [33, 246, 161, 160, 159, 158, 157, 173, 133];
const LEFT_EYE_UPPER_LID = [263, 466, 388, 387, 386, 385, 384, 398, 362];
const RIGHT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];
const LEFT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];
const RIGHT_BROW = [70, 63, 105, 66, 107];
const LEFT_BROW = [336, 296, 334, 293, 300];
const OUTER_LIP = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];

interface EyeMaskConfig {
  lidIndices: readonly number[];
  browIndices: readonly number[];
  outerIdx: number;
  innerIdx: number;
  lidLift: number;
  browTighten: number;
}

function createMask(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function point(detection: LipDetection, idx: number): { x: number; y: number } | null {
  const p = detection.landmarks[idx];
  if (!p) return null;
  return { x: p.x * detection.imageWidth, y: p.y * detection.imageHeight };
}

function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[]
): boolean {
  let started = false;
  for (const idx of indices) {
    const p = point(detection, idx);
    if (!p) continue;
    if (!started) {
      ctx.moveTo(p.x, p.y);
      started = true;
    } else {
      ctx.lineTo(p.x, p.y);
    }
  }
  if (started) ctx.closePath();
  return started;
}

function smoothClosedPolygon(
  ctx: CanvasRenderingContext2D,
  points: readonly { x: number; y: number }[]
): void {
  if (points.length < 3) return;
  const midpoint = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2
  });
  const start = midpoint(points[points.length - 1]!, points[0]!);
  ctx.moveTo(start.x, start.y);
  for (let i = 0; i < points.length; i++) {
    const cp = points[i]!;
    const end = midpoint(points[i]!, points[(i + 1) % points.length]!);
    ctx.quadraticCurveTo(cp.x, cp.y, end.x, end.y);
  }
  ctx.closePath();
}

function eyeUpUnit(
  detection: LipDetection,
  outerIdx: number,
  innerIdx: number
): { x: number; y: number } {
  const outer = point(detection, outerIdx);
  const inner = point(detection, innerIdx);
  if (!outer || !inner) return { x: 0, y: -1 };
  const ax = inner.x - outer.x;
  const ay = inner.y - outer.y;
  const len = Math.hypot(ax, ay);
  if (len < 0.01) return { x: 0, y: -1 };
  let ux = -ay / len;
  let uy = ax / len;
  if (uy > 0) {
    ux = -ux;
    uy = -uy;
  }
  return { x: ux, y: uy };
}

function drawMobileLidPath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  config: EyeMaskConfig
): void {
  const lid = config.lidIndices
    .map((idx) => point(detection, idx))
    .filter((p): p is { x: number; y: number } => Boolean(p));
  const brow = config.browIndices
    .map((idx) => point(detection, idx))
    .filter((p): p is { x: number; y: number } => Boolean(p));
  if (lid.length < 3 || brow.length < 2) return;

  const up = eyeUpUnit(detection, config.outerIdx, config.innerIdx);
  const liftedLid = lid.map((l) => {
    let nearestBrow = brow[0]!;
    let minD = Infinity;
    for (const b of brow) {
      const d = (b.x - l.x) ** 2 + (b.y - l.y) ** 2;
      if (d < minD) {
        minD = d;
        nearestBrow = b;
      }
    }
    const reach = (nearestBrow.x - l.x) * up.x + (nearestBrow.y - l.y) * up.y;
    return {
      x: l.x + up.x * reach * config.lidLift,
      y: l.y + up.y * reach * config.lidLift
    };
  });

  const tightenedBrow = brow.map((b) => {
    let nearestLid = liftedLid[0]!;
    let minD = Infinity;
    for (const l of liftedLid) {
      const d = (l.x - b.x) ** 2 + (l.y - b.y) ** 2;
      if (d < minD) {
        minD = d;
        nearestLid = l;
      }
    }
    const reach = -((nearestLid.x - b.x) * up.x + (nearestLid.y - b.y) * up.y);
    return {
      x: b.x - up.x * reach * config.browTighten,
      y: b.y - up.y * reach * config.browTighten
    };
  });

  const loop: { x: number; y: number }[] = [];
  for (const p of liftedLid) loop.push(p);
  for (let i = tightenedBrow.length - 1; i >= 0; i--) loop.push(tightenedBrow[i]!);
  smoothClosedPolygon(ctx, loop);
}

type SemanticFaceMapMode = 'full' | 'fast';
const FAST_SEMANTIC_WORK_MAX = 960;

function blurRadius(value: number, mode: SemanticFaceMapMode): number {
  return mode === 'fast' ? value * 0.45 : value;
}

function detectionForMode(
  detection: LipDetection,
  mode: SemanticFaceMapMode
): LipDetection {
  if (mode !== 'fast') return detection;
  const scale = Math.min(1, FAST_SEMANTIC_WORK_MAX / Math.max(detection.imageWidth, detection.imageHeight));
  if (scale >= 1) return detection;
  return {
    ...detection,
    imageWidth: Math.max(1, Math.round(detection.imageWidth * scale)),
    imageHeight: Math.max(1, Math.round(detection.imageHeight * scale))
  };
}

function filledPathMask(
  detection: LipDetection,
  indices: readonly number[],
  featherRatio = 0,
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  if (tracePath(ctx, detection, indices)) ctx.fill();
  if (featherRatio > 0) {
    stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(1, blurRadius(Math.min(mask.width, mask.height) * featherRatio, mode)));
  }
  return mask;
}

function mobileLidMask(
  detection: LipDetection,
  config: EyeMaskConfig,
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  drawMobileLidPath(ctx, detection, config);
  ctx.fill();
  stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(2, blurRadius(Math.min(mask.width, mask.height) * 0.015, mode)));
  return mask;
}

function creaseFadeMask(
  detection: LipDetection,
  config: EyeMaskConfig,
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  drawMobileLidPath(ctx, detection, {
    ...config,
    lidLift: config.lidLift * 1.18,
    browTighten: Math.max(0.18, config.browTighten * 0.62)
  });
  ctx.fill();

  const hardLid = mobileLidMask(detection, {
    ...config,
    lidLift: config.lidLift * 0.72,
    browTighten: Math.min(0.72, config.browTighten * 1.18)
  }, mode);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.drawImage(hardLid, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(2, blurRadius(Math.min(mask.width, mask.height) * 0.02, mode)));
  return mask;
}

function lashLineMask(
  detection: LipDetection,
  indices: readonly number[],
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  const pts = indices
    .map((idx) => point(detection, idx))
    .filter((p): p is { x: number; y: number } => Boolean(p));
  if (pts.length < 2) return mask;

  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(1.2, Math.min(mask.width, mask.height) * 0.004);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  pts.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();
  stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(0.6, blurRadius(Math.min(mask.width, mask.height) * 0.0015, mode)));
  return mask;
}

function browMask(
  detection: LipDetection,
  indices: readonly number[],
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  const points = indices
    .map((idx) => point(detection, idx))
    .filter((p): p is { x: number; y: number } => Boolean(p));
  if (points.length < 2) return mask;

  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(5, Math.min(mask.width, mask.height) * 0.014);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  points.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();
  stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(0.8, blurRadius(Math.min(mask.width, mask.height) * 0.003, mode)));
  return mask;
}

function lipBorderMask(
  detection: LipDetection,
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;
  const pts = OUTER_LIP
    .map((idx) => point(detection, idx))
    .filter((p): p is { x: number; y: number } => Boolean(p));
  if (pts.length < 3) return mask;

  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(2.4, Math.min(mask.width, mask.height) * 0.0055);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  smoothClosedPolygon(ctx, pts);
  ctx.stroke();
  stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(0.8, blurRadius(Math.min(mask.width, mask.height) * 0.002, mode)));
  return mask;
}

function cheekMask(
  detection: LipDetection,
  apexIdx: number,
  auxIndices: readonly number[]
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d');
  if (!ctx) return mask;

  const apex = point(detection, apexIdx);
  if (!apex) return mask;
  let x = apex.x * 2;
  let y = apex.y * 2;
  let weight = 2;
  for (const idx of auxIndices) {
    const p = point(detection, idx);
    if (!p) continue;
    x += p.x;
    y += p.y;
    weight++;
  }
  x /= weight;
  y /= weight;

  const leftTemple = point(detection, 234);
  const rightTemple = point(detection, 454);
  const faceWidth = leftTemple && rightTemple
    ? Math.abs(rightTemple.x - leftTemple.x)
    : mask.width * 0.5;
  const radius = Math.max(32, faceWidth * 0.25);
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.48, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  return mask;
}

// Forehead hair guard: covers everything above the eyebrows with a soft
// vertical gradient. This prevents the skin / veil / blush passes from
// painting over bangs or forehead hair in the landmark-pseudo fallback path,
// where no ONNX hair mask is available. The ONNX path later replaces this
// with its own data-driven hair mask.
function hairGuardMask(
  detection: LipDetection,
  mode: SemanticFaceMapMode = 'full'
): HTMLCanvasElement {
  const mask = createMask(detection.imageWidth, detection.imageHeight);
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return mask;

  const leftBrowPts = LEFT_BROW.map((idx) => point(detection, idx)).filter((p): p is { x: number; y: number } => Boolean(p));
  const rightBrowPts = RIGHT_BROW.map((idx) => point(detection, idx)).filter((p): p is { x: number; y: number } => Boolean(p));
  if (leftBrowPts.length === 0 && rightBrowPts.length === 0) return mask;

  const allBrowY = [...leftBrowPts, ...rightBrowPts].map((p) => p.y);
  const minYBrow = Math.min(...allBrowY);

  const gradient = ctx.createLinearGradient(0, 0, 0, minYBrow);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, mask.width, minYBrow);
  if (mode === 'fast') {
    stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(1, blurRadius(Math.min(mask.width, mask.height) * 0.004, mode)));
  } else {
    stackBlur(ctx, 0, 0, mask.width, mask.height, Math.max(1, Math.min(mask.width, mask.height) * 0.004));
  }
  return mask;
}

function subtract(mask: HTMLCanvasElement, cutouts: HTMLCanvasElement[]): HTMLCanvasElement {
  const out = createMask(mask.width, mask.height);
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.drawImage(mask, 0, 0);
  ctx.globalCompositeOperation = 'destination-out';
  for (const cutout of cutouts) ctx.drawImage(cutout, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  return out;
}

export function measureSemanticMaskCoverage(
  masks: SemanticFaceMap['masks']
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(masks).map(([key, mask]) => [key, maskAlphaCoverage(mask)])
  );
}

// Core makeup regions that must carry real coverage for a landmark-pseudo map
// to be trustworthy. If a region collapses (degenerate landmarks, occlusion),
// its absence pulls confidence down instead of reporting a flat fixed value.
const CORE_SEMANTIC_REGIONS = ['skin', 'lips', 'leftEye', 'rightEye', 'leftCheek', 'rightCheek'];

// Replaces the previous hard-coded 0.62. Landmark-pseudo masks top out lower
// than the ONNX parser by construction, so we map full core coverage to 0.70
// and degrade linearly toward 0.45 as core regions drop out.
function estimateLandmarkConfidence(coverage: Record<string, number>): number {
  const present = CORE_SEMANTIC_REGIONS.filter((key) => (coverage[key] ?? 0) > 0.002).length;
  const ratio = present / CORE_SEMANTIC_REGIONS.length;
  return Number((0.45 + 0.25 * ratio).toFixed(2));
}

export async function buildSemanticFaceMap(
  _source: ImageBitmap | HTMLCanvasElement,
  detection: LipDetection,
  options: { mode?: SemanticFaceMapMode } = {}
): Promise<SemanticFaceMap> {
  const mode = options.mode ?? 'full';
  const maskDetection = detectionForMode(detection, mode);
  const faceOval = filledPathMask(maskDetection, FACE_OVAL, 0.006, mode);
  const rightEye = filledPathMask(maskDetection, RIGHT_EYE, 0.004, mode);
  const leftEye = filledPathMask(maskDetection, LEFT_EYE, 0.004, mode);
  const rightBrow = browMask(maskDetection, RIGHT_BROW, mode);
  const leftBrow = browMask(maskDetection, LEFT_BROW, mode);
  const lips = filledPathMask(maskDetection, OUTER_LIP, 0.003, mode);
  const hairGuard = hairGuardMask(maskDetection, mode);
  const rightEyeConfig: EyeMaskConfig = {
    lidIndices: RIGHT_EYE_UPPER_LID,
    browIndices: RIGHT_BROW,
    outerIdx: 33,
    innerIdx: 133,
    lidLift: 0.30,
    browTighten: 0.42
  };
  const leftEyeConfig: EyeMaskConfig = {
    lidIndices: LEFT_EYE_UPPER_LID,
    browIndices: LEFT_BROW,
    outerIdx: 263,
    innerIdx: 362,
    lidLift: 0.30,
    browTighten: 0.42
  };
  const rightMobileLid = mobileLidMask(maskDetection, rightEyeConfig, mode);
  const leftMobileLid = mobileLidMask(maskDetection, leftEyeConfig, mode);
  const rightCreaseFade = creaseFadeMask(maskDetection, rightEyeConfig, mode);
  const leftCreaseFade = creaseFadeMask(maskDetection, leftEyeConfig, mode);
  const rightLashLine = lashLineMask(maskDetection, RIGHT_EYE_UPPER_LID, mode);
  const leftLashLine = lashLineMask(maskDetection, LEFT_EYE_UPPER_LID, mode);

  const skin = subtract(faceOval, [rightEye, leftEye, rightBrow, leftBrow, lips, hairGuard]);

  const masks = {
    faceOval,
    skin,
    leftEye,
    rightEye,
    leftMobileLid,
    rightMobileLid,
    leftCreaseFade,
    rightCreaseFade,
    leftLashLine,
    rightLashLine,
    leftBrow,
    rightBrow,
    lips,
    lipBorder: lipBorderMask(maskDetection, mode),
    leftCheek: cheekMask(maskDetection, 50, [101, 36]),
    rightCheek: cheekMask(maskDetection, 280, [330, 266]),
    hair: hairGuard
  };

  const coverage = measureSemanticMaskCoverage(masks);

  return {
    width: maskDetection.imageWidth,
    height: maskDetection.imageHeight,
    source: 'landmark-pseudo',
    masks,
    quality: {
      confidence: estimateLandmarkConfidence(coverage),
      parserAvailable: false,
      coverage,
      fallbacks: ['all landmark-pseudo']
    }
  };
}
